import { useState, useEffect } from 'react';

/**
 * A component that displays JavaScript errors in the UI for debugging
 */
export function ErrorDisplay() {
  const [errors, setErrors] = useState<Array<{ message: string, timestamp: Date }>>([]);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    // Store the original console.error function
    const originalConsoleError = console.error;
    
    // Create a custom error handler that doesn't override console.error
    const handleCustomError = (...args: any[]) => {
      const errorMessage = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');
      
      setTimeout(() => {
        setErrors(prev => [...prev, { message: errorMessage, timestamp: new Date() }]);
      }, 0);
    };
    
    // Listen for custom error events instead of overriding console.error
    window.addEventListener('yapzap-error', (event: any) => {
      handleCustomError(event.detail);
    });

    // Capture runtime errors
    const handleError = (event: ErrorEvent) => {
      setTimeout(() => {
        setErrors(prev => [...prev, { 
          message: `Runtime Error: ${event.message} at ${event.filename}:${event.lineno}:${event.colno}`, 
          timestamp: new Date() 
        }]);
      }, 0);
      
      // Prevent the browser from showing its own error dialog
      event.preventDefault();
    };

    // Capture promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      setTimeout(() => {
        setErrors(prev => [...prev, { 
          message: `Unhandled Promise Rejection: ${event.reason}`, 
          timestamp: new Date() 
        }]);
      }, 0);
      
      // Prevent the browser from showing its own error dialog
      event.preventDefault();
    };

    // Add event listeners
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    // Cleanup
    return () => {
      window.removeEventListener('yapzap-error', handleCustomError);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  if (errors.length === 0) return null;

  return (
    <div 
      className="fixed bottom-0 right-0 z-50 bg-red-600 text-white p-3 m-4 max-w-xl rounded-lg shadow-lg"
      style={{ maxHeight: isMinimized ? '40px' : '300px', overflow: 'auto', transition: 'max-height 0.3s ease-in-out' }}
    >
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-bold">🔴 JavaScript Errors ({errors.length})</h3>
        <div className="space-x-2">
          <button 
            onClick={() => setIsMinimized(!isMinimized)} 
            className="text-white hover:text-gray-300"
          >
            {isMinimized ? '🔼' : '🔽'}
          </button>
          <button 
            onClick={() => setErrors([])} 
            className="text-white hover:text-gray-300"
          >
            Clear
          </button>
        </div>
      </div>
      
      {!isMinimized && (
        <div className="max-h-56 overflow-y-auto">
          {errors.map((error, index) => (
            <div key={index} className="mb-2 pb-2 border-b border-red-400 last:border-0">
              <div className="text-xs text-red-200">
                {error.timestamp.toLocaleTimeString()}
              </div>
              <pre className="text-sm whitespace-pre-wrap">{error.message}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ErrorDisplay;